import { CapabilityRegistry } from '@upl/provenance-core';
import {
  C2PA_ADAPTER_DESCRIPTOR,
  C2PA_OFFICIAL_TRUST_LIST_DESCRIPTOR,
  C2PA_OFFICIAL_TSA_TRUST_LIST_DESCRIPTOR,
  GOOGLE_AI_CONTENT_DETECTION_DESCRIPTOR,
  GOOGLE_SYNTHID_DIRECT_DETECTION_DESCRIPTOR,
  METADATA_ADAPTER_DESCRIPTOR,
  OPENAI_PROVENANCE_DESCRIPTOR,
  type EvidenceAdapterDescriptor,
} from '@upl/evidence-adapter-runtime';

const registry = new CapabilityRegistry();

registry.register({
  ...runtimeFields(C2PA_ADAPTER_DESCRIPTOR),
  label: 'C2PA Content Credentials',
  description: 'C2PA credentials in JPEG and PNG are supported and checked locally.',
});
registry.register({
  ...runtimeFields(METADATA_ADAPTER_DESCRIPTOR),
  label: 'Embedded image metadata',
  description: 'A safe EXIF, XMP, and IPTC subset is supported and inspected locally.',
});
registry.register({
  ...runtimeFields(C2PA_OFFICIAL_TRUST_LIST_DESCRIPTOR),
  state: 'NOT_CHECKED',
  label: 'Official C2PA signer trust',
  description: 'The official C2PA Trust List is evaluated by the server API; this local browser report does not transmit the image.',
});
registry.register({
  ...runtimeFields(C2PA_OFFICIAL_TSA_TRUST_LIST_DESCRIPTOR),
  state: 'NOT_CHECKED',
  label: 'Official C2PA timestamp trust',
  description: 'The official C2PA TSA Trust List is evaluated by the server API; this local browser report does not transmit the image.',
});
registry.register({
  ...runtimeFields(OPENAI_PROVENANCE_DESCRIPTOR),
  label: 'OpenAI provenance',
  description: 'The official OpenAI API is supported server-side when explicitly configured; it is off in this browser runtime.',
});
registry.register({
  ...runtimeFields(GOOGLE_AI_CONTENT_DETECTION_DESCRIPTOR),
  label: 'Google AI Content Detection',
  description: 'Private Preview access is restricted and is not checked in this release.',
});
registry.register({
  ...runtimeFields(GOOGLE_SYNTHID_DIRECT_DETECTION_DESCRIPTOR),
  label: 'Google SynthID',
  description: 'Direct SynthID Detector access is restricted and is not checked in this release.',
});
registry.register({
  id: 'proprietary.watermarks',
  label: 'Proprietary watermarks',
  mediaTypes: ['image/jpeg', 'image/png'],
  state: 'NOT_CHECKED',
  description: 'Proprietary provider watermarks are not checked in this release.',
  available: false,
  local: false,
});
registry.register({
  id: 'media.video-audio',
  label: 'Video and audio',
  mediaTypes: ['video/*', 'audio/*'],
  state: 'NOT_SUPPORTED',
  description: 'Video and audio are not supported in this release.',
  available: false,
  local: true,
});

export const LOCAL_RUNTIME_CAPABILITIES = registry.list();
export const SERVER_RUNTIME_CAPABILITIES = registry.list().map((capability) => ({
  ...capability,
  local: false,
  description: capability.description
    .replace(/checked locally\./i, 'checked by the ephemeral server verifier.')
    .replace(/inspected locally\./i, 'inspected by the ephemeral server verifier.')
    .replace(/this local browser report does not transmit the image\./i, 'This measured run used the ephemeral server verifier; the image was not retained.'),
}));

function runtimeFields(descriptor: Readonly<EvidenceAdapterDescriptor>) {
  const available = descriptor.availability === 'AVAILABLE';
  return {
    id: descriptor.id,
    mediaTypes: descriptor.media_types,
    state: available ? 'SUPPORTED' as const : 'NOT_CHECKED' as const,
    available,
    local: !descriptor.network.requiresNetwork,
  };
}
